/**
 * forceOffline — debug tool for tester scenarios where real connectivity
 * loss can't be easily simulated (e.g. a desktop tester at home who can't
 * walk into a basement / military installation).
 *
 * Activation:
 *   1. URL flag: append `?offline=force` to any route once → persists in
 *      localStorage until toggled off.
 *   2. Settings → Debug toggle (Profile page).
 *
 * Implementation:
 *   - An axios request interceptor checks the flag and rejects with a
 *     synthetic network error before the request leaves the browser.
 *   - The error shape matches what axios produces for real offline cases,
 *     so `isNetworkError()` in ProfileContext / syncQueue routes it into
 *     the same enqueue path as a true disconnection.
 *
 * Bypass list:
 *   - `/api/auth/*` — the user must still be able to log in/out while
 *     debugging, and the auth flow has its own caching layer.
 */

import axios from 'axios';

const FLAG_KEY = 'enferno_force_offline_v1';

const readFlag = () => {
  try { return localStorage.getItem(FLAG_KEY) === '1'; } catch { return false; }
};

export const isForceOffline = () => readFlag();

export const setForceOffline = (on) => {
  try {
    if (on) localStorage.setItem(FLAG_KEY, '1');
    else localStorage.removeItem(FLAG_KEY);
  } catch { /* ignore */ }
  // Broadcast so the OfflineDebugToggle (or any listener) re-renders.
  try { window.dispatchEvent(new Event('enferno:force-offline-changed')); } catch { /* ignore */ }
  // When turning the simulator OFF, manually kick the sync drain — the real
  // browser was never offline, so no `online` event will fire. Without this,
  // queued items sit forever until the next login or actual disconnection.
  if (!on) {
    import('./syncQueue').then((m) => m.drain()).catch(() => { /* idb not ready */ });
  }
};

const isAuthUrl = (url) => typeof url === 'string' && url.includes('/api/auth/');

const syntheticNetworkError = (config) => {
  const err = new Error('Network Error');
  err.code = 'ERR_NETWORK';
  err.config = config;
  err.request = {};
  err.response = undefined;
  return err;
};

let installed = false;

export const installForceOfflineInterceptor = () => {
  if (installed) return;
  installed = true;

  // Pick up the URL flag on first call (called once from App.js mount).
  try {
    const params = new URLSearchParams(window.location.search);
    const flagVal = params.get('offline');
    if (flagVal === 'force') setForceOffline(true);
    else if (flagVal === 'off') setForceOffline(false);
  } catch { /* ignore */ }

  axios.interceptors.request.use((config) => {
    if (!readFlag()) return config;
    if (isAuthUrl(config.url)) return config; // auth bypasses sim
    return Promise.reject(syntheticNetworkError(config));
  });
};
